import { useCallback, useEffect, useRef, useState } from "react";
import { DOMAIN } from "../constants";
import { isHushPhrase } from "../utils/hushPhrase";
import {
  startAiSession,
  endAiSession,
  reportAiTurn,
} from "../services/aiSessionService";
import { BUILD_VERSION } from "../buildInfo";

/* ── Research log (AiSession) ───────────────────────────────────
   Every chat with the robot is logged on the server (text only — never the
   audio) so it can be reviewed ✓/✗ later. A SESSION spans from the moment the
   robot is turned on until it is turned off:
     • hands-free: double-tap → single tap (or the page going away)
     • single-shot: a run of long-press commands; closed after this much
       silence (mirrors the server's conversation-context TTL) */
const SINGLE_SESSION_IDLE_MS = 90 * 1000;

/* Conversation history is OWNED BY THE SERVER (a single JSON file). This hook
   only DRIVES it: after a command resolves it tells the server to clear the file
   (command succeeded → performCommand returns { history: "clear" }) or append
   the turn (show_cheapest → { history: "append", summary }). A failed/not-
   understood command ({ history: "keep" }) leaves the file untouched so the
   context survives a retry. The server reads that file for GPT context and
   applies its own inactivity (staleness) reset. */

/* ── Audio-engine tuning ─────────────────────────────────────────
   The wave UI reads a smoothed 0..1 `level` (mic RMS while you speak, TTS RMS
   while the AI speaks). Hands-free uses the SAME RMS (raw 0..127 units, like the
   AI page) against `micThreshold` for voice-activity detection. */
const QUIET_RMS = 1.5; // RMS at/below this counts as silence
const SILENCE_MS = 1000; // continuous silence that ends a hands-free utterance
const LEVEL_DIVISOR = 38; // RMS → 0..1 normalization for the waves
const LEVEL_SMOOTH = 0.4; // wave smoothing (higher = snappier)

/* ── Voice barge-in (hands-free only) ──────────────────────────────
   The user may talk OVER the robot: the robot stops and goes back to
   LISTENING; whatever the user keeps saying is then captured by the normal
   VAD like any utterance (just "די" → nothing is captured → silence).

   How "the user is talking" is told apart from the robot's own voice coming
   back through the speaker: the hands-free mic stream is opened with
   echoCancellation, but phones differ a lot in how much residual echo is left
   — and in how much they attenuate the USER while the far end plays. So the
   threshold is ADAPTIVE, learned per reply: during the first moments of
   playback (the user is presumably still quiet) we measure how much mic
   signal each unit of TTS loudness leaves behind (the echo ratio) and the
   residual floor; afterwards the mic must exceed that expected echo by a clear
   margin — or an absolute minimum — for a short sustained window. */
const BARGE_IN_GRACE_MS = 350; // learn the echo here; no cut yet
const BARGE_IN_SUSTAIN_MS = 200; // must stay above the threshold this long
const BARGE_IN_ECHO_MARGIN = 1.8; // × the expected residual echo
const BARGE_IN_MIN_RMS = 8; // absolute floor of the threshold
const BARGE_IN_RMS_FACTOR = 0.8; // × the user's micThreshold (the VAD setting)
const BARGE_IN_MAX_RATIO = 1.2; // cap on the learned echo ratio (a polluted window)
const BARGE_IN_MEMORY = 0.7; // how much of the previous reply's estimate carries over
const BARGE_IN_MIN_SEGMENT_MS = 1300; // a capture right after a cut that ends at once → noise, drop
const BARGE_IN_AFTER_CUT_MS = 1500; // "right after a cut" window

/**
 * Voice engine for the bottom-nav robot coin.
 *
 *  • Long-press (single-shot): "idle" → "starting" → "recording" → "processing".
 *  • Hands-free (continuous):  double-tap opens the mic; a VAD loop records each
 *    utterance, auto-sends on silence, plays the reply, then resumes listening.
 *    A single tap exits. Multi-turn context survives via the server history file.
 *  • `level` (a ref, 0..1) + `mode` drive the audio-reactive wave animation in
 *    BOTH modes: mode ∈ "idle"|"listening"|"recording"|"processing"|"speaking".
 *
 * The AI page is NOT touched — this only reuses the /general-ai endpoints.
 *
 * @param {object} opts
 * @param {Array}      opts.pages          [{ index, label, route }]
 * @param {string[]}   opts.categories
 * @param {string[][]} opts.subCategories
 * @param {string[]}   opts.supermarkets   distinct chain names (compare by name)
 * @param {string[]}   opts.shelfProducts  shelf-target names (find_product)
 * @param {string}     opts.ttsLanguage    "he" | "en"
 * @param {string}     opts.ttsVoice
 * @param {number}     opts.micThreshold    VAD RMS threshold (hands-free)
 * @param {(cmd:object, transcript:string)=>void} opts.onCommand
 */
export default function useVoiceCommand({
  pages,
  categories,
  subCategories,
  supermarkets,
  shelfProducts,
  shelfSessionProducts,
  ttsLanguage,
  ttsVoice,
  micThreshold,
  onCommand,
}) {
  const [state, setState] = useState("idle"); // single-shot phase
  const [error, setError] = useState(null);
  const [handsFree, setHandsFree] = useState(false);
  const [mode, setMode] = useState("idle"); // drives waves + hint

  const stateRef = useRef("idle");
  const setPhase = useCallback((p) => {
    stateRef.current = p;
    setState(p);
  }, []);

  // recording / stream
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const pendingStopRef = useRef(false); // a stop requested during "starting"

  // hands-free + shared flags
  const handsFreeRef = useRef(false);
  const speakingRef = useRef(false); // AI TTS currently playing
  const busyRef = useRef(false); // hands-free upload in flight
  const segActiveRef = useRef(false); // a hands-free utterance is being captured
  const quietSinceRef = useRef(null);
  // voice barge-in bookkeeping
  const speakStartedAtRef = useRef(0); // when the current TTS clip started
  const loudSinceRef = useRef(null); // the user has been loud over the robot since…
  const echoRatioRef = useRef(0); // learned: mic RMS per unit of TTS RMS (residual echo)
  const echoFloorRef = useRef(0); // learned: mic RMS floor while the robot plays
  const lastCutAtRef = useRef(0); // when the robot was last cut off by the user
  const segStartedAtRef = useRef(0); // when the current hands-free capture started
  const bargeInSegRef = useRef(false); // the current capture started right after a cut

  // web-audio analysis
  const audioCtxRef = useRef(null);
  const micSrcRef = useRef(null);
  const micAnalyserRef = useRef(null);
  const micRafRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const ttsSrcRef = useRef(null);
  const ttsAnalyserRef = useRef(null);
  const ttsRafRef = useRef(null);

  // wave level (0..1) + current visual mode (read by VoiceWaves via the ref)
  const levelRef = useRef(0);
  const modeRef = useRef("idle");

  // research log: the open session + the turn whose reply is playing
  const sessionRef = useRef({ id: null, mode: null, promise: null });
  const lastTurnRef = useRef(null); // { sessionId, turnId }
  const singleIdleTimerRef = useRef(null);

  // latest config/handler (avoid stale closures inside async callbacks)
  const cfgRef = useRef({});
  cfgRef.current = {
    pages,
    categories,
    subCategories,
    supermarkets,
    shelfProducts,
    shelfSessionProducts,
    ttsLanguage,
    ttsVoice,
    micThreshold,
    onCommand,
  };

  const refreshMode = useCallback(() => {
    let m;
    if (speakingRef.current) m = "speaking";
    else if (segActiveRef.current || stateRef.current === "recording")
      m = "recording";
    else if (busyRef.current || stateRef.current === "processing")
      m = "processing";
    else if (handsFreeRef.current) m = "listening";
    else m = "idle";
    if (m !== modeRef.current) {
      modeRef.current = m;
      setMode(m);
    }
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const ensureCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      try {
        audioCtxRef.current = new AC();
      } catch (e) {
        return null;
      }
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  }, []);

  const rms = (analyser, buf) => {
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const d = buf[i] - 128;
      sum += d * d;
    }
    return Math.sqrt(sum / buf.length) || 0;
  };

  const pushLevel = (value) => {
    const target = Math.min(value / LEVEL_DIVISOR, 1);
    levelRef.current += (target - levelRef.current) * LEVEL_SMOOTH;
  };

  /* ── Research log: session lifecycle ── */
  const closeSession = useCallback((reason) => {
    clearTimeout(singleIdleTimerRef.current);
    singleIdleTimerRef.current = null;
    const s = sessionRef.current;
    const id = s.id;
    sessionRef.current = { id: null, mode: null, promise: null };
    lastTurnRef.current = null;
    if (id) endAiSession(id, reason);
  }, []);

  /* the open session's id for `mode` — opening one (once) if needed; a
     mode switch (a long-press session, then a double-tap) closes the old one */
  const ensureSession = useCallback(
    async (mode) => {
      const s = sessionRef.current;
      if (s.id && s.mode === mode) return s.id;
      if (s.promise && s.mode === mode) return s.promise;
      if (s.id || s.promise) closeSession("user");
      const cfg = cfgRef.current;
      const promise = startAiSession({
        mode,
        version: BUILD_VERSION,
        ttsLanguage: cfg.ttsLanguage || "he",
        ttsVoice: cfg.ttsVoice || "",
      }).then((id) => {
        if (sessionRef.current.promise === promise) {
          sessionRef.current = { id, mode, promise: null };
        }
        return id;
      });
      sessionRef.current = { id: null, mode, promise };
      return promise;
    },
    [closeSession]
  );

  /* a single-shot session stays open while commands keep coming */
  const touchSingleSession = useCallback(() => {
    clearTimeout(singleIdleTimerRef.current);
    singleIdleTimerRef.current = setTimeout(() => {
      if (sessionRef.current.mode === "single") closeSession("idle");
    }, SINGLE_SESSION_IDLE_MS);
  }, [closeSession]);

  /* the user cut the robot off (tap / barge-in) → mark the turn */
  const markInterrupted = useCallback(() => {
    const t = lastTurnRef.current;
    if (t) reportAiTurn(t.sessionId, t.turnId, { interrupted: true });
  }, []);

  /* Persist the conversation outcome to the server-owned history file. */
  const persistHistory = useCallback((body) => {
    return fetch(`${DOMAIN}/api/v1/general-ai/history`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(() => {});
  }, []);

  const pickMime = () => {
    const prefs = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg",
    ];
    for (const m of prefs) {
      if (
        window.MediaRecorder.isTypeSupported &&
        window.MediaRecorder.isTypeSupported(m)
      ) {
        return m;
      }
    }
    return "";
  };

  /* ── TTS playback routed through an analyser (drives the speaking waves) ── */
  const stopReply = useCallback(() => {
    if (ttsRafRef.current) {
      cancelAnimationFrame(ttsRafRef.current);
      ttsRafRef.current = null;
    }
    if (ttsAudioRef.current) {
      try {
        ttsAudioRef.current.pause();
      } catch (e) {
        /* ignore */
      }
    }
    try {
      ttsSrcRef.current && ttsSrcRef.current.disconnect();
    } catch (e) {
      /* ignore */
    }
    try {
      ttsAnalyserRef.current && ttsAnalyserRef.current.disconnect();
    } catch (e) {
      /* ignore */
    }
    ttsSrcRef.current = null;
    ttsAnalyserRef.current = null;
    speakingRef.current = false;
  }, []);

  const playReply = useCallback(
    async (url) => {
      stopReply(); // never overlap a previous reply
      let audio;
      try {
        audio = new Audio(url);
        audio.crossOrigin = "anonymous";
      } catch (e) {
        return;
      }
      ttsAudioRef.current = audio;

      let analyser = null;
      let buf = null;
      const ctx = ensureCtx();
      if (ctx) {
        try {
          const src = ctx.createMediaElementSource(audio);
          analyser = ctx.createAnalyser();
          analyser.fftSize = 2048;
          src.connect(analyser);
          analyser.connect(ctx.destination); // else audio is silent
          buf = new Uint8Array(analyser.frequencyBinCount);
          ttsSrcRef.current = src;
          ttsAnalyserRef.current = analyser;
        } catch (e) {
          analyser = null; // analysis unavailable → element still plays
        }
      }

      const endSpeaking = () => {
        speakingRef.current = false;
        levelRef.current = 0;
        if (ttsRafRef.current) {
          cancelAnimationFrame(ttsRafRef.current);
          ttsRafRef.current = null;
        }
        refreshMode();
      };

      audio.addEventListener("play", () => {
        speakingRef.current = true;
        speakStartedAtRef.current = Date.now();
        loudSinceRef.current = null;
        // start from a faded copy of the last reply's echo estimate, then
        // re-learn during this reply's grace window
        echoRatioRef.current *= BARGE_IN_MEMORY;
        echoFloorRef.current *= BARGE_IN_MEMORY;
        refreshMode();
        if (analyser && buf) {
          const step = () => {
            if (audio.paused || audio.ended) return;
            pushLevel(rms(analyser, buf));
            ttsRafRef.current = requestAnimationFrame(step);
          };
          ttsRafRef.current = requestAnimationFrame(step);
        }
      });
      audio.addEventListener("ended", endSpeaking);
      audio.addEventListener("pause", endSpeaking);

      try {
        if (ctx && ctx.state === "suspended") await ctx.resume();
        await audio.play();
      } catch (e) {
        endSpeaking();
      }
    },
    [ensureCtx, refreshMode, stopReply]
  );

  /* ── Upload an utterance → STT/AI/TTS → execute + persist history ── */
  const sendBlob = useCallback(
    async (isHandsFree) => {
      const recorder = mediaRecorderRef.current;
      const mime = (recorder && recorder.mimeType) || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mime });
      chunksRef.current = [];

      /* A capture that started right after the user cut the robot off but
         ended almost at once held no real words (the tail of "די", a cough) —
         drop it quietly instead of sending noise and getting a spoken
         "didn't understand". */
      const bargeIn = bargeInSegRef.current;
      bargeInSegRef.current = false;
      if (
        isHandsFree &&
        bargeIn &&
        Date.now() - segStartedAtRef.current < BARGE_IN_MIN_SEGMENT_MS
      ) {
        busyRef.current = false;
        refreshMode();
        return;
      }

      if (!blob.size) {
        if (isHandsFree) {
          busyRef.current = false;
          refreshMode();
        } else {
          setPhase("idle");
        }
        return;
      }

      const ext = mime.includes("mp4")
        ? "mp4"
        : mime.includes("ogg")
        ? "ogg"
        : "webm";

      if (isHandsFree) {
        busyRef.current = true;
        refreshMode();
      }

      /* the research-log session this utterance belongs to */
      let sessionId = null;
      try {
        sessionId = await ensureSession(isHandsFree ? "handsfree" : "single");
      } catch (e) {
        sessionId = null;
      }

      const cfg = cfgRef.current;
      const fd = new FormData();
      fd.append("file", new File([blob], `voice.${ext}`, { type: mime }));
      if (sessionId) fd.append("sessionId", sessionId);
      fd.append(
        "context",
        JSON.stringify({
          pages: cfg.pages || [],
          categories: cfg.categories || [],
          subCategories: cfg.subCategories || [],
          supermarkets: cfg.supermarkets || [],
          shelfProducts: cfg.shelfProducts || [],
          shelfSessionProducts: cfg.shelfSessionProducts || [],
        })
      );
      fd.append("ttsLanguage", cfg.ttsLanguage || "he");
      if (cfg.ttsVoice) fd.append("ttsVoice", cfg.ttsVoice);
      // History is NOT sent — the server reads it from its own JSON file.

      try {
        const res = await fetch(`${DOMAIN}/api/v1/general-ai/command`, {
          method: "POST",
          body: fd,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        /* "שתוק" / "די" → the answer is silence. The server already returns no
           audio for its "hush" intent; this guards the case where it classified
           the phrase as "none" and sent a polite "didn't understand" clip. */
        const hushOnly =
          (json.command && json.command.actionType === "hush") ||
          isHushPhrase(json.transcript);
        if (hushOnly) {
          json.audioUrl = null;
          json.command = { actionType: "hush", speech: "" };
        }

        /* the server logged this utterance as a turn; if it had to open a
           fresh session (ours was unknown / already closed) adopt that one */
        if (json.sessionId && json.turnId) {
          if (json.sessionId !== sessionRef.current.id) {
            sessionRef.current = {
              id: json.sessionId,
              mode: isHandsFree ? "handsfree" : "single",
              promise: null,
            };
          }
          lastTurnRef.current = {
            sessionId: json.sessionId,
            turnId: json.turnId,
          };
        }
        const pageRoute =
          typeof window !== "undefined" ? window.location.pathname : "";

        if (json.audioUrl) {
          await playReply(`${DOMAIN}/${json.audioUrl}`);
        }

        // Execute the action. performCommand returns a directive for the history
        // JSON: "clear" (succeeded), "append" (show_cheapest → record the list),
        // or "keep" (failed / not understood → leave context for a retry).
        let directive = { history: "keep" };
        let handlerFailed = false;
        if (json.command && typeof cfg.onCommand === "function") {
          try {
            const r = await cfg.onCommand(json.command, json.transcript);
            if (r && r.history) directive = r;
          } catch (e) {
            handlerFailed = true;
          }
        }

        /* research log: what actually happened with the command */
        if (json.sessionId && json.turnId) {
          const action = (json.command && json.command.actionType) || "none";
          const status = handlerFailed
            ? "failed"
            : directive.outcome ||
              (action === "hush"
                ? "silent"
                : action === "none"
                ? "not_understood"
                : directive.history === "keep"
                ? "ignored"
                : "done");
          reportAiTurn(json.sessionId, json.turnId, {
            outcome: {
              status,
              detail: directive.detail || directive.summary || "",
              history: directive.history,
            },
            pageRoute,
          });
        }
        if (!isHandsFree) touchSingleSession();

        const transcript = (json.transcript || "").trim();
        if (directive.history === "clear") {
          await persistHistory({ action: "clear" });
        } else if (directive.history === "append" && transcript) {
          const assistantContent =
            (directive.summary && String(directive.summary)) ||
            (json.command && json.command.speech) ||
            "";
          await persistHistory({
            action: "append",
            user: transcript,
            assistant: assistantContent,
          });
        }
        // "keep" → leave the file unchanged
      } catch (e) {
        setError("network");
      } finally {
        if (isHandsFree) {
          busyRef.current = false;
          refreshMode();
        } else {
          setPhase("idle");
        }
      }
    },
    [
      setPhase,
      persistHistory,
      playReply,
      refreshMode,
      ensureSession,
      touchSingleSession,
    ]
  );

  /* ── Mic analyser: feeds the waves, and (hands-free) the VAD loop ── */
  const stopMicAnalyser = useCallback(() => {
    if (micRafRef.current) {
      cancelAnimationFrame(micRafRef.current);
      micRafRef.current = null;
    }
    try {
      micSrcRef.current && micSrcRef.current.disconnect();
    } catch (e) {
      /* ignore */
    }
    try {
      micAnalyserRef.current && micAnalyserRef.current.disconnect();
    } catch (e) {
      /* ignore */
    }
    micSrcRef.current = null;
    micAnalyserRef.current = null;
    if (!speakingRef.current) levelRef.current = 0;
  }, []);

  const startMicAnalyser = useCallback(
    (stream, vad) => {
      const ctx = ensureCtx();
      if (!ctx) return;
      let analyser;
      try {
        const src = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        src.connect(analyser); // NOT to destination (no mic feedback)
        micSrcRef.current = src;
        micAnalyserRef.current = analyser;
      } catch (e) {
        return;
      }
      const buf = new Uint8Array(analyser.frequencyBinCount);

      const startSegment = () => {
        const s = streamRef.current;
        if (!s) return;
        let recorder;
        try {
          const m = pickMime();
          recorder = m
            ? new MediaRecorder(s, { mimeType: m })
            : new MediaRecorder(s);
        } catch (e) {
          return;
        }
        chunksRef.current = [];
        mediaRecorderRef.current = recorder;
        segActiveRef.current = true;
        quietSinceRef.current = null;
        segStartedAtRef.current = Date.now();
        bargeInSegRef.current =
          Date.now() - lastCutAtRef.current < BARGE_IN_AFTER_CUT_MS;
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size) chunksRef.current.push(e.data);
        };
        recorder.onstop = () => {
          segActiveRef.current = false;
          refreshMode();
          sendBlob(true);
        };
        recorder.start();
        refreshMode();
      };

      const endSegment = () => {
        quietSinceRef.current = null;
        segActiveRef.current = false; // stop the loop re-checking silence
        try {
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state !== "inactive"
          ) {
            mediaRecorderRef.current.stop(); // onstop → sendBlob
          }
        } catch (e) {
          /* ignore */
        }
      };

      const loop = () => {
        const level = rms(analyser, buf);
        if (!speakingRef.current) pushLevel(level); // TTS owns level while speaking

        /* voice barge-in: the user talks over the robot → cut the speech and
           go back to listening (the normal VAD below picks up whatever the
           user keeps saying from the very next frame) */
        if (
          vad &&
          handsFreeRef.current &&
          speakingRef.current &&
          !segActiveRef.current
        ) {
          const now = Date.now();
          const ttsRms = levelRef.current * LEVEL_DIVISOR; // the robot's loudness right now
          if (now - speakStartedAtRef.current < BARGE_IN_GRACE_MS) {
            // learn this phone's residual echo while the user is (still) quiet
            if (ttsRms > 2) {
              echoRatioRef.current = Math.min(
                BARGE_IN_MAX_RATIO,
                Math.max(echoRatioRef.current, level / ttsRms)
              );
            }
            echoFloorRef.current = Math.min(
              cfgRef.current.micThreshold || 15,
              Math.max(echoFloorRef.current, level)
            );
            loudSinceRef.current = null;
          } else {
            const expectedEcho =
              echoRatioRef.current * ttsRms + echoFloorRef.current * 0.5;
            const thr = Math.max(
              BARGE_IN_MIN_RMS,
              (cfgRef.current.micThreshold || 15) * BARGE_IN_RMS_FACTOR,
              expectedEcho * BARGE_IN_ECHO_MARGIN
            );
            if (level >= thr) {
              if (!loudSinceRef.current) loudSinceRef.current = now;
              else if (now - loudSinceRef.current >= BARGE_IN_SUSTAIN_MS) {
                loudSinceRef.current = null;
                lastCutAtRef.current = now;
                stopReply(); // hush — the 'pause' event finishes the bookkeeping
                levelRef.current = 0;
                refreshMode(); // → "listening" at once
                markInterrupted();
              }
            } else {
              loudSinceRef.current = null;
            }
          }
        }

        if (vad && handsFreeRef.current && !speakingRef.current) {
          const thr = cfgRef.current.micThreshold || 15;
          if (!segActiveRef.current && !busyRef.current && level >= thr) {
            startSegment();
          } else if (segActiveRef.current) {
            if (level <= QUIET_RMS) {
              if (!quietSinceRef.current) quietSinceRef.current = Date.now();
              else if (Date.now() - quietSinceRef.current >= SILENCE_MS) {
                endSegment();
              }
            } else {
              quietSinceRef.current = null;
            }
          }
        }
        micRafRef.current = requestAnimationFrame(loop);
      };

      ctx
        .resume()
        .then(() => {
          micRafRef.current = requestAnimationFrame(loop);
        })
        .catch(() => {
          micRafRef.current = requestAnimationFrame(loop);
        });
    },
    [ensureCtx, refreshMode, sendBlob, stopReply, markInterrupted]
  );

  /* ── Single-shot recording (long-press) ── */
  const startRecording = useCallback(async () => {
    if (stateRef.current !== "idle" || handsFreeRef.current) return;
    setError(null);
    pendingStopRef.current = false;

    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError("unsupported");
      return;
    }

    setPhase("starting");

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setError("mic-denied");
      setPhase("idle");
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];

    let recorder;
    try {
      const m = pickMime();
      recorder = m
        ? new MediaRecorder(stream, { mimeType: m })
        : new MediaRecorder(stream);
    } catch (e) {
      releaseStream();
      setError("unsupported");
      setPhase("idle");
      return;
    }

    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      stopMicAnalyser();
      releaseStream();
      sendBlob(false);
    };
    recorder.start();
    setPhase("recording");
    startMicAnalyser(stream, false); // waves only
    refreshMode();

    // Honor a tap-to-stop that arrived while mic permission was resolving.
    if (pendingStopRef.current) {
      pendingStopRef.current = false;
      setPhase("processing");
      refreshMode();
      try {
        recorder.stop();
      } catch (e) {
        stopMicAnalyser();
        releaseStream();
        setPhase("idle");
        refreshMode();
      }
    }
  }, [
    setPhase,
    releaseStream,
    sendBlob,
    startMicAnalyser,
    stopMicAnalyser,
    refreshMode,
  ]);

  const stopRecording = useCallback(() => {
    if (stateRef.current === "starting") {
      pendingStopRef.current = true;
      return;
    }
    if (stateRef.current !== "recording") return;
    setPhase("processing");
    refreshMode();
    try {
      if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
    } catch (e) {
      stopMicAnalyser();
      releaseStream();
      setPhase("idle");
      refreshMode();
    }
  }, [setPhase, releaseStream, stopMicAnalyser, refreshMode]);

  /* ── Hands-free continuous mode ── */
  const startHandsFree = useCallback(async () => {
    if (handsFreeRef.current || stateRef.current !== "idle") return;
    setError(null);

    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError("unsupported");
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (e) {
      setError("mic-denied");
      return;
    }
    streamRef.current = stream;
    handsFreeRef.current = true;
    setHandsFree(true);
    segActiveRef.current = false;
    busyRef.current = false;
    quietSinceRef.current = null;
    refreshMode();
    startMicAnalyser(stream, true); // VAD on
    ensureSession("handsfree"); // the chat starts now (robot turned on)
  }, [refreshMode, startMicAnalyser, ensureSession]);

  const stopHandsFree = useCallback(() => {
    handsFreeRef.current = false;
    setHandsFree(false);
    quietSinceRef.current = null;
    // Discard any in-flight utterance (don't send a half segment on exit).
    const rec = mediaRecorderRef.current;
    if (rec) {
      try {
        rec.onstop = null;
        if (rec.state !== "inactive") rec.stop();
      } catch (e) {
        /* ignore */
      }
    }
    chunksRef.current = [];
    segActiveRef.current = false;
    busyRef.current = false;
    stopMicAnalyser();
    stopReply();
    releaseStream();
    levelRef.current = 0;
    refreshMode();
    closeSession("user"); // the chat ends here (robot turned off)
  }, [stopMicAnalyser, stopReply, releaseStream, refreshMode, closeSession]);

  /* ── "Hush": cut the AI's speech RIGHT NOW ──
     Tap-to-interrupt. Stops the TTS element (its 'pause' event runs
     endSpeaking → mode refresh), drops the wave level, and leaves everything
     else as it was: hands-free keeps listening for the next utterance, a
     single-shot command is already idle. The command the reply described has
     already been executed (or is executing) — only the voice is cut.
     Returns true when there was speech to cut. */
  const stopSpeaking = useCallback(() => {
    const hadSpeech =
      speakingRef.current ||
      (ttsAudioRef.current &&
        !ttsAudioRef.current.paused &&
        !ttsAudioRef.current.ended);
    if (!hadSpeech) return false;
    stopReply();
    levelRef.current = 0;
    refreshMode();
    markInterrupted();
    return true;
  }, [stopReply, refreshMode, markInterrupted]);

  /* Explicit "reset the conversation" action (also exposed to the caller). */
  const clearHistory = useCallback(
    () => persistHistory({ action: "clear" }),
    [persistHistory]
  );

  /* Append a turn whose assistant side is only known AFTER an async action
     finishes (e.g. the compare ranking, computed by the overlay) — so the next
     spoken command can reference it ("switch to the second one"). */
  const appendHistory = useCallback(
    (user, assistant) =>
      persistHistory({
        action: "append",
        user: String(user || ""),
        assistant: String(assistant || ""),
      }),
    [persistHistory]
  );

  /* Speak an arbitrary line in the reply voice (server TTS), routed through the
     same analyser so the waves react. Used to voice a result summary the app
     composes AFTER an action runs (e.g. the cart-optimization outcome). */
  const speak = useCallback(
    async (text) => {
      const msg = (text || "").trim();
      if (!msg) return;
      try {
        /* name the turn this line belongs to → logged as its follow-up */
        const t = lastTurnRef.current || {};
        const res = await fetch(`${DOMAIN}/api/v1/general-ai/speak`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: msg,
            ttsLanguage,
            ttsVoice,
            sessionId: t.sessionId || undefined,
            turnId: t.turnId || undefined,
          }),
        });
        const json = await res.json();
        if (json && json.audioUrl) {
          await playReply(`${DOMAIN}/${json.audioUrl}`);
        }
      } catch (e) {
        /* a missing summary voice shouldn't break the flow */
      }
    },
    [playReply, ttsLanguage, ttsVoice]
  );

  // auto-clear transient errors so the hint chip doesn't linger
  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(null), 3500);
    return () => clearTimeout(t);
  }, [error]);

  // the page going away (tab closed, phone locked long enough) ends the chat
  useEffect(() => {
    const onHide = () => {
      if (sessionRef.current.id) closeSession("unload");
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [closeSession]);

  // cleanup on unmount
  useEffect(
    () => () => {
      closeSession("unload");
      try {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.onstop = null;
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        /* ignore */
      }
      if (micRafRef.current) cancelAnimationFrame(micRafRef.current);
      if (ttsRafRef.current) cancelAnimationFrame(ttsRafRef.current);
      try {
        if (ttsAudioRef.current) ttsAudioRef.current.pause();
      } catch (e) {
        /* ignore */
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      try {
        if (audioCtxRef.current) audioCtxRef.current.close();
      } catch (e) {
        /* ignore */
      }
    },
    [closeSession]
  );

  return {
    state,
    error,
    mode,
    level: levelRef,
    handsFree,
    startRecording,
    stopRecording,
    startHandsFree,
    stopHandsFree,
    stopSpeaking,
    clearHistory,
    appendHistory,
    speak,
  };
}
